import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Tag, Percent, Calendar, Gift, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from "../lib/api";


const typeLabels = {
  fixed: 'Фиксированная скидка',
  percent: 'Процентная скидка',
  free_days: 'Бесплатные дни',
};

const typeIcons = {
  fixed: Tag,
  percent: Percent,
  free_days: Gift,
};

const initialFormState = {
  code: '',
  type: 'fixed',
  value: '',
  max_uses: '',
  valid_from: '',
  valid_until: '',
  channel_id: '',
  tariff_id: '',
};

export default function PromoCodes() {
  const [codes, setCodes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchData = async () => {
    try {
      const [codesRes, channelsRes, tariffsRes] = await Promise.all([
        api.get(`/promo-codes`),
        api.get(`/channels`),
        api.get(`/tariffs`),
      ]);
      setCodes(codesRes.data);
      setChannels(channelsRes.data);
      setTariffs(tariffsRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingCode(null);
    setFormData(initialFormState);
    setModalOpen(true);
  };

  const openEditModal = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      type: code.type,
      value: code.value.toString(),
      max_uses: code.max_uses?.toString() || '',
      valid_from: code.valid_from?.slice(0, 16) || '',
      valid_until: code.valid_until?.slice(0, 16) || '',
      channel_id: code.channel_id || '',
      tariff_id: code.tariff_id || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      code: formData.code,
      type: formData.type,
      value: parseFloat(formData.value),
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
      valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
      channel_id: formData.channel_id || null,
      tariff_id: formData.tariff_id || null,
    };

    try {
      if (editingCode) {
        await api.put(`/promo-codes/${editingCode.id}`, payload);
        toast.success('Промокод обновлен');
      } else {
        await api.post(`/promo-codes`, payload);
        toast.success('Промокод создан');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Удалить промокод "${code.code}"?`)) return;

    try {
      await api.delete(`/promo-codes/${code.id}`);
      toast.success('Промокод удален');
      fetchData();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await api.put(`/promo-codes/${code.id}`, { is_active: !code.is_active });
      fetchData();
      toast.success(code.is_active ? 'Промокод деактивирован' : 'Промокод активирован');
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getChannelName = (channelId) => {
    if (!channelId) return 'Все каналы';
    const channel = channels.find(c => c.id === channelId);
    return channel?.name || 'Неизвестный канал';
  };

  const formatValue = (code) => {
    if (code.type === 'fixed') return `−${code.value} ₽`;
    if (code.type === 'percent') return `−${code.value}%`;
    if (code.type === 'free_days') return `+${code.value} дней`;
    return code.value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="promo-codes-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Промокоды</h1>
          <p className="text-zinc-500">Управление скидками и акциями</p>
        </div>
        <Button 
          onClick={openCreateModal} 
          className="glow-primary"
          data-testid="add-promo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Создать промокод
        </Button>
      </div>

      {codes.length > 0 ? (
        <div className="card">
          <table className="data-table data-table-responsive">
            <thead>
              <tr>
                <th>Код</th>
                <th>Тип</th>
                <th>Значение</th>
                <th>Использовано</th>
                <th>Ограничения</th>
                <th>Статус</th>
                <th className="text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => {
                const Icon = typeIcons[code.type] || Tag;
                return (
                  <tr key={code.id} data-testid={`promo-row-${code.id}`}>
                    <td data-label="Код">
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-[#2AABEE]/10 text-[#2AABEE] px-3 py-1 rounded font-mono font-bold">
                          {code.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => copyCode(code.code)}
                        >
                          {copiedCode === code.code ? (
                            <Check className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </td>
                    <td data-label="Тип">
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Icon className="w-4 h-4" />
                        {typeLabels[code.type]}
                      </div>
                    </td>
                    <td data-label="Значение">
                      <span className="text-emerald-400 font-medium">
                        {formatValue(code)}
                      </span>
                    </td>
                    <td data-label="Использовано">
                      <span className="text-zinc-300">
                        {code.used_count}
                        {code.max_uses && ` / ${code.max_uses}`}
                      </span>
                    </td>
                    <td data-label="Ограничения" className="text-sm text-zinc-500">
                      {getChannelName(code.channel_id)}
                    </td>
                    <td data-label="Статус">
                      <Switch
                        checked={code.is_active}
                        onCheckedChange={() => handleToggleActive(code)}
                      />
                    </td>
                    <td data-label="Действия">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(code)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(code)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Tag className="empty-state-icon" />
            <p className="mb-4">Промокоды не созданы</p>
            <Button onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Создать первый промокод
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? 'Редактировать промокод' : 'Создать промокод'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="code">Код</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SALE20"
                  required
                  className="font-mono uppercase"
                  data-testid="promo-code-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Тип скидки</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger className="bg-[#121212] border-white/10" data-testid="promo-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Фиксированная (₽)</SelectItem>
                      <SelectItem value="percent">Процент (%)</SelectItem>
                      <SelectItem value="free_days">Бесплатные дни</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="value">
                    {formData.type === 'fixed' ? 'Сумма скидки (₽)' : 
                     formData.type === 'percent' ? 'Процент скидки' : 
                     'Количество дней'}
                  </Label>
                  <Input
                    id="value"
                    type="number"
                    min="0"
                    step={formData.type === 'percent' ? '1' : '0.01'}
                    max={formData.type === 'percent' ? '100' : undefined}
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder={formData.type === 'percent' ? '20' : '500'}
                    required
                    data-testid="promo-value-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="max_uses">Максимум использований (пусто = безлимит)</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="100"
                  data-testid="promo-max-uses-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid_from">Действует с</Label>
                  <Input
                    id="valid_from"
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    data-testid="promo-valid-from-input"
                  />
                </div>
                <div>
                  <Label htmlFor="valid_until">Действует до</Label>
                  <Input
                    id="valid_until"
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    data-testid="promo-valid-until-input"
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                <h4 className="text-sm font-medium text-white mb-3">Ограничения (необязательно)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Только для канала</Label>
                    <Select
                      value={formData.channel_id}
                      onValueChange={(value) => setFormData({ ...formData, channel_id: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger className="bg-[#121212] border-white/10">
                        <SelectValue placeholder="Все каналы" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все каналы</SelectItem>
                        {channels.map((channel) => (
                          <SelectItem key={channel.id} value={channel.id}>
                            {channel.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Только для тарифа</Label>
                    <Select
                      value={formData.tariff_id}
                      onValueChange={(value) => setFormData({ ...formData, tariff_id: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger className="bg-[#121212] border-white/10">
                        <SelectValue placeholder="Все тарифы" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все тарифы</SelectItem>
                        {tariffs.map((tariff) => (
                          <SelectItem key={tariff.id} value={tariff.id}>
                            {tariff.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-promo-btn">
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
